export const FIELD_MAPPING_TRACE_EVENT = 'fg_field_mapping_trace'

export type FieldMappingTraceEntry = {
  ts: number
  phase: string
  message?: string
  data?: any
  origin?: 'main' | 'worker'
}

type TraceEntryInput = Omit<FieldMappingTraceEntry, 'ts'> & { ts?: number }

const truthy = (v: any): boolean => {
  const s = String(v ?? '').trim().toLowerCase()
  return s === '1' || s === 'true' || s === 'yes' || s === 'on'
}

export function isFieldMappingDebugEnabled(): boolean {
  try {
    const g = globalThis as any
    if (g && g.__FG_DEBUG_FIELD_MAPPING__ === true) return true
  } catch { }
  if (typeof window === 'undefined') return false
  try {
    const sp = new URLSearchParams(window.location.search)
    const raw =
      sp.get('field_mapping_debug') ||
      sp.get('debug_field_mapping') ||
      sp.get('debug_fields') ||
      sp.get('field_map_debug') ||
      ''
    if (truthy(raw)) return true
    if (truthy(window.localStorage.getItem('fg_debug_field_mapping'))) return true
  } catch { }
  return false
}

export function isFieldMappingConsoleEnabled(): boolean {
  try {
    const g = globalThis as any
    if (g && g.__FG_DEBUG_FIELD_MAPPING_CONSOLE__ === true) return true
  } catch { }
  if (typeof window === 'undefined') return false
  try {
    const sp = new URLSearchParams(window.location.search)
    const raw =
      sp.get('field_mapping_debug_console') ||
      sp.get('debug_field_mapping_console') ||
      sp.get('debug_fields_console') ||
      ''
    if (truthy(raw)) return true
    if (truthy(window.localStorage.getItem('fg_debug_field_mapping_console'))) return true
  } catch { }
  return false
}

export function isFieldMappingVerboseEnabled(): boolean {
  try {
    const g = globalThis as any
    if (g && g.__FG_DEBUG_FIELD_MAPPING_VERBOSE__ === true) return true
  } catch { }
  if (typeof window === 'undefined') return false
  try {
    const sp = new URLSearchParams(window.location.search)
    const raw =
      sp.get('field_mapping_debug_verbose') ||
      sp.get('debug_field_mapping_verbose') ||
      sp.get('debug_fields_verbose') ||
      ''
    if (truthy(raw)) return true
    if (truthy(window.localStorage.getItem('fg_debug_field_mapping_verbose'))) return true
  } catch { }
  return false
}

export function setFieldMappingConsoleEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (enabled) window.localStorage.setItem('fg_debug_field_mapping_console', '1')
    else window.localStorage.removeItem('fg_debug_field_mapping_console')
  } catch { }
  try {
    ;(globalThis as any).__FG_DEBUG_FIELD_MAPPING_CONSOLE__ = !!enabled
  } catch { }
}

export function logFieldMapping(message: string, data?: any): void {
  if (!isFieldMappingDebugEnabled()) return
  if (!isFieldMappingConsoleEnabled()) return
  try {
    if (data === undefined) console.log('[FG-FIELD-MAP]', message)
    else console.log('[FG-FIELD-MAP]', message, data)
  } catch { }
}

export function getFieldMappingTrace(): FieldMappingTraceEntry[] {
  try {
    const g = globalThis as any
    const arr = Array.isArray(g.__FG_FIELD_MAPPING_TRACE__) ? g.__FG_FIELD_MAPPING_TRACE__ : []
    return arr.slice()
  } catch {
    return []
  }
}

export function clearFieldMappingTrace(): void {
  try {
    const g = globalThis as any
    g.__FG_FIELD_MAPPING_TRACE__ = []
  } catch { }
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(FIELD_MAPPING_TRACE_EVENT))
    } catch { }
  }
}

const appendTrace = (entry: FieldMappingTraceEntry): void => {
  try {
    const g = globalThis as any
    const arr = Array.isArray(g.__FG_FIELD_MAPPING_TRACE__) ? g.__FG_FIELD_MAPPING_TRACE__ : []
    if (arr.length > 5000) arr.splice(0, arr.length - 4500)
    arr.push(entry)
    g.__FG_FIELD_MAPPING_TRACE__ = arr
  } catch { }
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(FIELD_MAPPING_TRACE_EVENT))
    } catch { }
  }
}

export function pushFieldMappingTrace(entry: TraceEntryInput): void {
  if (!isFieldMappingDebugEnabled()) return
  try {
    const isWorker = typeof window === 'undefined'
    const origin: 'main' | 'worker' = entry.origin ? entry.origin : (isWorker ? 'worker' : 'main')
    const normalized: FieldMappingTraceEntry = {
      ts: Number(entry.ts ?? Date.now()),
      phase: String(entry.phase ?? ''),
      message: entry.message,
      data: entry.data,
      origin,
    }
    if (isWorker && typeof (globalThis as any)?.postMessage === 'function') {
      try {
        ;(globalThis as any).postMessage({ type: 'FIELD_MAPPING_TRACE', payload: normalized })
      } catch { }
    }
    appendTrace(normalized)
  } catch { }
}
